import { createMarkdownRenderer } from './markdown_renderer';

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export default function AlpineRemarkEditor() {
  Alpine.data(
    'remarkEditor',
    (promptId, content, placeholder, versionNumber = 1) => ({
      promptId: promptId,
      content: content || '',
      placeholder: placeholder || 'Start writing your markdown...',
      currentVersionNumber: versionNumber,
      isReleasing: false,
      characterCount: 0,
      autosaveDebounced: null,
      renderer: null,
      isAutoSaving: false,
      isSaved: false,
      errorMessage: null,
      errorCode: null,

      async init() {
        await this.$nextTick();

        // Initialize character count with current content
        this.characterCount = (this.content || '').length;

        // Set up the markdown renderer
        this.renderer = createMarkdownRenderer();

        // Initial render
        await this.updatePreview();

        // Set up real-time preview updates
        this.setupRealTimePreview();

        // Set up debounced autosave function
        this.autosaveDebounced = debounce(async (markdown) => {
          // Only run autosave if promptId exists (create is temp id for new prompt)
          if (this.promptId === 'create') {
            return;
          }

          console.log('Autosaving...');
          this.isAutoSaving = true;

          try {
            // Ensure we always send a valid text value, even when empty
            const textToSend = markdown || '';

            await fetch('/api/auto-save/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken':
                  document.querySelector('[name=csrf-token]').content,
              },
              body: JSON.stringify({
                promptId: this.promptId,
                text: textToSend,
              }),
            });
            this.isSaved = true;
          } catch (error) {
            console.error('Autosave failed:', error);
          } finally {
            this.isAutoSaving = false;
          }
        }, 5000);
      },

      setupRealTimePreview() {
        const textarea = this.$refs['markdown-source'];
        if (textarea) {
          textarea.addEventListener('input', async () => {
            this.content = textarea.value;
            await this.updatePreview();
            this.autosaveDebounced(this.content);
          });
        }
      },

      async updatePreview() {
        try {
          const html = await this.renderer.render(this.content);
          const preview = this.$refs['markdown-preview'];
          this.characterCount = this.content.length;
          if (preview) {
            preview.innerHTML = html;
          }
        } catch (error) {
          console.error('Error processing markdown:', error);
          const preview = this.$refs['markdown-preview'];
          if (preview) {
            preview.innerHTML =
              '<div class="text-red-500">Error rendering markdown</div>';
          }
        }
      },
      async releaseVersion() {
        console.log('Release version called');
        this.isReleasing = true;
        const response = await fetch('/api/release-version/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': document.querySelector('[name=csrf-token]').content,
          },
          body: JSON.stringify({
            promptId: this.promptId,
            text: this.content,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          this.currentVersionNumber = data.version_number;
        } else {
          console.error('Release version failed');
        }
        this.isReleasing = false;
      },
      async createPrompt() {
        try {
          this.errorMessage = null;
          this.errorCode = null;
          this.isReleasing = true;
          const currentMarkdown = this.content;

          // Get form data from Alpine components
          const titleElement = document.querySelector('[x-data*="title:"]');
          const categoryElement = document.querySelector(
            '[x-data*="selectedCategory:"]',
          );
          const modelElement = document.querySelector(
            '[x-data*="selectedModel:"]',
          );
          const tagsElement = document.querySelector(
            '[x-data*="selectedTags:"]',
          );

          const title = titleElement ? Alpine.$data(titleElement).title : '';

          // Check if title is empty and show warning dialog
          if (!title || title.trim() === '') {
            const shouldProceed = await this.showTitleWarningDialog();
            if (!shouldProceed) {
              return; // User cancelled
            }
            // Set default title for saving
            if (titleElement) {
              Alpine.$data(titleElement).title = 'Untitled Prompt';
            }
          }

          const formData = {
            title: titleElement
              ? Alpine.$data(titleElement).title
              : 'Untitled Prompt',
            text: currentMarkdown,
            category: categoryElement
              ? Alpine.$data(categoryElement).selectedCategory
              : null,
            model_name: modelElement
              ? Alpine.$data(modelElement).selectedModel
              : '',
            tags: tagsElement ? Alpine.$data(tagsElement).selectedTags : [],
          };

          console.log('Creating prompt with data:', formData);

          const response = await fetch('/api/prompts/create/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken':
                document.querySelector('[name=csrf-token]').content,
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(formData),
          });

          const contentType = response.headers.get('content-type') || '';
          const hasJson = contentType.includes('application/json');
          const data = hasJson ? await response.json() : null;

          if (response.ok) {
            console.log('Prompt created successfully:', data);

            // Redirect to the new prompt's edit page
            if (data && data.prompt_id) {
              window.location.href = `/prompts/${data.prompt_id}/`;
            } else {
              this.$dispatch('create-error', {
                message: 'Prompt created but response was unexpected',
              });
            }
          } else {
            console.error('Prompt creation failed', data);
            const errorMessage =
              (data && (data.error || data.detail)) ||
              'Failed to create prompt';

            if (response.status === 403 && data && data.code === 'tier_limit') {
              this.errorMessage = errorMessage;
              this.errorCode = data.code;
            } else {
              this.errorMessage = errorMessage;
              this.errorCode = data ? data.code || null : null;
            }

            this.$dispatch('create-error', {
              message: errorMessage,
              code: this.errorCode,
            });
          }
        } catch (error) {
          console.error('Prompt creation error:', error);
          this.$dispatch('create-error', {
            message: 'Failed to create prompt',
          });
          this.errorMessage = 'Failed to create prompt';
          this.errorCode = null;
        } finally {
          this.isReleasing = false;
        }
      },

      showTitleWarningDialog() {
        return new Promise((resolve) => {
          // Create dialog HTML with proper event handling
          const dialogHTML = `
                    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                         x-data="{ open: true, result: null }"
                         x-show="open"
                         x-cloak
                         x-transition:enter="transition ease-out duration-300"
                         x-transition:enter-start="opacity-0"
                         x-transition:enter-end="opacity-100"
                         x-transition:leave="transition ease-in duration-200"
                         x-transition:leave-start="opacity-100"
                         x-transition:leave-end="opacity-0">
                        <div class="bg-background text-foreground rounded-lg border shadow-lg p-6 max-w-md w-full mx-4"
                             x-show="open"
                             x-transition:enter="transition ease-out duration-300"
                             x-transition:enter-start="opacity-0 scale-95"
                             x-transition:enter-end="opacity-100 scale-100"
                             x-transition:leave="transition ease-in duration-200"
                             x-transition:leave-start="opacity-100 scale-100"
                             x-transition:leave-end="opacity-0 scale-95">
                            <div class="flex items-center gap-3 mb-4">
                                <i class="ph ph-warning text-yellow-500 text-xl"></i>
                                <h3 class="text-lg font-semibold">Save without title?</h3>
                            </div>
                            <p class="text-muted-foreground mb-6">
                                You're about to save this prompt without a title. It will be saved as "Untitled Prompt".
                                Are you sure you want to continue?
                            </p>
                            <div class="flex gap-3 justify-end">
                                <button @click="result = false; open = false"
                                        class="btn-secondary">
                                    Cancel
                                </button>
                                <button @click="result = true; open = false"
                                        class="btn-primary">
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                `;

          // Create temporary container and append dialog
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = dialogHTML;
          document.body.appendChild(tempContainer);

          // Start Alpine on the new content
          Alpine.initTree(tempContainer);

          // Get the Alpine component to watch for changes
          const alpineComponent = Alpine.$data(
            tempContainer.querySelector('[x-data]'),
          );

          // Watch for the result and resolve the promise
          const checkResult = () => {
            if (alpineComponent.result !== null) {
              const result = alpineComponent.result;
              // Clean up
              tempContainer.remove();
              resolve(result);
            } else if (alpineComponent.open === false) {
              // Dialog was closed without setting result (e.g., clicking outside)
              tempContainer.remove();
              resolve(false);
            } else {
              // Keep checking
              setTimeout(checkResult, 100);
            }
          };

          // Start checking for result
          checkResult();

          // Clean up after dialog is closed (fallback)
          const observer = new MutationObserver(() => {
            if (!tempContainer.querySelector('[x-data]')) {
              tempContainer.remove();
              observer.disconnect();
            }
          });
          observer.observe(tempContainer, { childList: true, subtree: true });
        });
      },
    }),
  );
}
